import {
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  Group,
  Modal,
  NumberInput,
  Select,
  Table,
  Tabs,
  TextInput,
  Title,
} from '@mantine/core';
import { IconDeviceFloppy, IconPlus, IconSettings, IconX } from '@tabler/icons-react';
import currencyCodes from 'currency-codes/data';
import { useEffect, useState } from 'react';
import prettifiedLabel from '../../constants/labelPrettifier';

const SHARED_UNITS = ['monthlyActiveUsers', 'subscribers', 'pageViews', 'whatsappMessages', 'textMessages', 'seats'];

const DYNAMIC_ONLY_UNITS = ['viberMessages', 'attributedRevenue'];

// They count SENT messages and are no longer written — kept selectable so a plan
// already priced on one still shows its own unit.
const DEPRECATED_UNITS = ['viberTextMessages', 'viberMultimediaMessages'];

const unitOptions = (units) =>
  units.map((value) => ({
    label: DEPRECATED_UNITS.includes(value) ? `${prettifiedLabel(value)} (deprecated)` : prettifiedLabel(value),
    value,
  }));

const TIERED_UNIT_OPTIONS = unitOptions([...SHARED_UNITS, ...DEPRECATED_UNITS]);
const DYNAMIC_UNIT_OPTIONS = unitOptions([...SHARED_UNITS, ...DYNAMIC_ONLY_UNITS, ...DEPRECATED_UNITS]);

const DEFAULT_INCLUDED_PER_SUBSCRIBER = 10;

// `undefined` in a patch deletes the key instead of writing it: the API stores
// the edited plan verbatim, so an emptied optional field has to leave the object
// rather than be sent as an empty value.
const patched = (object, patch) => {
  const result = { ...object, ...patch };
  Object.keys(patch).forEach((key) => {
    if (patch[key] === undefined) {
      delete result[key];
    }
  });
  return result;
};

const SubscriptionCustomizer = ({ selectedSubscriptions, setSelectedSubscriptions }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSubscriptionsInner, setSelectedSubscriptionsInner] = useState(selectedSubscriptions);

  useEffect(() => {
    setSelectedSubscriptionsInner(selectedSubscriptions);
  }, [selectedSubscriptions]);

  const updateComponent = (i, component, j, patch) => {
    const updatedSubscriptions = JSON.parse(JSON.stringify(selectedSubscriptionsInner));
    updatedSubscriptions[i][component][j] = patched(updatedSubscriptions[i][component][j], patch);
    setSelectedSubscriptionsInner(updatedSubscriptions);
  };

  const setSubscription = (i, subscription) => {
    const updatedSubscriptions = JSON.parse(JSON.stringify(selectedSubscriptionsInner));
    updatedSubscriptions[i] = { ...updatedSubscriptions[i], ...subscription };
    setSelectedSubscriptionsInner(updatedSubscriptions);
  };

  const deleteComponent = (i, component, j) => {
    const updatedSubscriptions = JSON.parse(JSON.stringify(selectedSubscriptionsInner));
    updatedSubscriptions[i][component] = updatedSubscriptions[i][component].filter((_, index) => index !== j);
    setSelectedSubscriptionsInner(updatedSubscriptions);
  };

  const addComponent = (i, component) => {
    const updatedSubscriptions = JSON.parse(JSON.stringify(selectedSubscriptionsInner));
    updatedSubscriptions[i][component].push({});
    setSelectedSubscriptionsInner(updatedSubscriptions);
  };

  const currencyOptions = currencyCodes.map((data) => {
    return { label: `${data.currency} (${data.code})`, value: data.code };
  });

  return (
    <>
      <Button variant="default" disabled={selectedSubscriptions.length === 0} onClick={() => setModalOpen(true)}>
        <IconSettings size={16} /> Customize
      </Button>

      <Modal size="full" opened={modalOpen} onClose={() => setModalOpen(false)} title="Customize Subscriptions">
        <Box component="form" ta="left">
          <Tabs defaultValue="0">
            <Tabs.List mb="md">
              {selectedSubscriptionsInner.map((sub, i) => (
                <Tabs.Tab key={i} value={i.toString()}>
                  {sub.name}
                </Tabs.Tab>
              ))}
            </Tabs.List>

            {selectedSubscriptionsInner.map((sub, i) => (
              <Tabs.Panel key={i} value={i.toString()}>
                <div style={{ paddingBottom: 'var(--mantine-spacing-xl)' }}>
                  <Grid mb="md">
                    <Grid.Col span={{ base: 12, md: 3 }} style={{ display: 'flex', alignItems: 'center' }}>
                      <label>Subscription name</label>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 9 }}>
                      <TextInput
                        name="name"
                        value={sub.name || ''}
                        onChange={(e) => setSubscription(i, { [e.target.name]: e.target.value })}
                      />
                    </Grid.Col>
                  </Grid>
                  <Grid mb="md">
                    <Grid.Col span={{ base: 12, md: 3 }} style={{ display: 'flex', alignItems: 'center' }}>
                      <label>Subscription currency</label>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 9 }}>
                      <Select
                        data={currencyOptions}
                        onChange={(value) => setSubscription(i, { currency: value })}
                        value={sub.currency}
                        placeholder="Type to search..."
                        searchable
                      />
                    </Grid.Col>
                  </Grid>
                </div>
                <Divider my="sm" />
                <Title order={5}>Tiered Pricing</Title>
                {sub.subscriptionTieredPricings.length > 0 && (
                  <Table striped withTableBorder>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Tier Name</Table.Th>
                        <Table.Th>Price</Table.Th>
                        <Table.Th>Treshold</Table.Th>
                        <Table.Th>Unit</Table.Th>
                        <Table.Th></Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {sub.subscriptionTieredPricings.map((pricing, j) => (
                        <Table.Tr key={j}>
                          <Table.Td>
                            <TextInput
                              name="name"
                              value={pricing.name || ''}
                              onChange={(e) =>
                                updateComponent(i, 'subscriptionTieredPricings', j, { name: e.target.value })
                              }
                            />
                          </Table.Td>
                          <Table.Td>
                            <NumberInput
                              name="price"
                              min={0}
                              value={pricing.price}
                              onChange={(value) =>
                                updateComponent(i, 'subscriptionTieredPricings', j, { price: value })
                              }
                            />
                          </Table.Td>
                          <Table.Td>
                            <NumberInput
                              name="treshold"
                              min={0}
                              step={1}
                              value={pricing.treshold}
                              onChange={(value) =>
                                updateComponent(i, 'subscriptionTieredPricings', j, { treshold: value })
                              }
                            />
                          </Table.Td>
                          <Table.Td style={{ width: '25%' }}>
                            <Select
                              data={TIERED_UNIT_OPTIONS}
                              onChange={(value) => updateComponent(i, 'subscriptionTieredPricings', j, { unit: value })}
                              value={pricing.unit}
                              placeholder="Type to search..."
                              searchable
                              allowDeselect={false}
                            />
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Button
                              size="xs"
                              color="red"
                              onClick={() => deleteComponent(i, 'subscriptionTieredPricings', j)}
                            >
                              <IconX size={14} />
                            </Button>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
                <Button
                  size="xs"
                  variant="default"
                  onClick={() => addComponent(i, 'subscriptionTieredPricings')}
                  mt="sm"
                >
                  <IconPlus size={14} /> Add Pricing Component
                </Button>
                <Divider my="sm" />
                <Title order={5}>Dynamic Pricing</Title>
                {sub.subscriptionDynamicPricings.length > 0 && (
                  <Table striped withTableBorder>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Unit Price</Table.Th>
                        <Table.Th>Unit</Table.Th>
                        <Table.Th>Minimum</Table.Th>
                        <Table.Th>Email Overage</Table.Th>
                        <Table.Th></Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {sub.subscriptionDynamicPricings.map((pricing, j) => (
                        <Table.Tr key={j}>
                          <Table.Td>
                            <NumberInput
                              name="unitPrice"
                              min={0}
                              value={pricing.unitPrice}
                              description={
                                pricing.unit === 'attributedRevenue'
                                  ? 'A fraction of the attributed revenue: 0.05 is 5%.'
                                  : undefined
                              }
                              onChange={(value) =>
                                updateComponent(i, 'subscriptionDynamicPricings', j, { unitPrice: value })
                              }
                            />
                          </Table.Td>
                          <Table.Td style={{ width: '25%' }}>
                            <Select
                              data={DYNAMIC_UNIT_OPTIONS}
                              onChange={(value) =>
                                updateComponent(i, 'subscriptionDynamicPricings', j, {
                                  unit: value,
                                  ...(value === 'subscribers' ? {} : { emailOverage: undefined }),
                                })
                              }
                              value={pricing.unit}
                              placeholder="Type to search..."
                              searchable
                              allowDeselect={false}
                            />
                          </Table.Td>
                          <Table.Td>
                            {/* The API stores a decimal string, so the number is stringified on write. */}
                            <NumberInput
                              name="minimumAmount"
                              aria-label="Minimum"
                              placeholder="None"
                              min={0}
                              allowNegative={false}
                              value={pricing.minimumAmount ?? ''}
                              onChange={(value) =>
                                updateComponent(i, 'subscriptionDynamicPricings', j, {
                                  minimumAmount: value === '' ? undefined : String(value),
                                })
                              }
                            />
                          </Table.Td>
                          <Table.Td>
                            {pricing.unit === 'subscribers' && (
                              <>
                                <Checkbox
                                  label="Email overage"
                                  checked={!!pricing.emailOverage}
                                  onChange={(e) =>
                                    updateComponent(i, 'subscriptionDynamicPricings', j, {
                                      emailOverage: e.currentTarget.checked
                                        ? { includedPerSubscriber: DEFAULT_INCLUDED_PER_SUBSCRIBER }
                                        : undefined,
                                    })
                                  }
                                />
                                {pricing.emailOverage && (
                                  <Group gap="xs" mt="xs" wrap="nowrap" align="flex-start">
                                    <NumberInput
                                      label="Included per subscriber"
                                      name="includedPerSubscriber"
                                      min={0}
                                      step={1}
                                      allowDecimal={false}
                                      allowNegative={false}
                                      value={pricing.emailOverage.includedPerSubscriber ?? ''}
                                      onChange={(value) =>
                                        updateComponent(i, 'subscriptionDynamicPricings', j, {
                                          emailOverage: patched(pricing.emailOverage, {
                                            includedPerSubscriber: value === '' ? 0 : Number(value),
                                          }),
                                        })
                                      }
                                    />
                                    <NumberInput
                                      label="Price per email"
                                      name="pricePerEmail"
                                      min={0}
                                      allowNegative={false}
                                      value={pricing.emailOverage.pricePerEmail ?? ''}
                                      onChange={(value) =>
                                        updateComponent(i, 'subscriptionDynamicPricings', j, {
                                          emailOverage: patched(pricing.emailOverage, {
                                            pricePerEmail: value === '' ? undefined : String(value),
                                          }),
                                        })
                                      }
                                    />
                                  </Group>
                                )}
                              </>
                            )}
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Button
                              size="xs"
                              color="red"
                              onClick={() => deleteComponent(i, 'subscriptionDynamicPricings', j)}
                            >
                              <IconX size={14} />
                            </Button>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
                <Button
                  size="xs"
                  variant="default"
                  onClick={() => addComponent(i, 'subscriptionDynamicPricings')}
                  mt="sm"
                >
                  <IconPlus size={14} /> Add Pricing Component
                </Button>
              </Tabs.Panel>
            ))}
          </Tabs>

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setSelectedSubscriptions(selectedSubscriptionsInner);
                setModalOpen(false);
              }}
            >
              Save <IconDeviceFloppy size={16} />
            </Button>
          </Group>
        </Box>
      </Modal>
    </>
  );
};

export default SubscriptionCustomizer;
